import type {Code, Node, Text, PhrasingContent, Parent} from 'mdast';

import type {BenchlingTagConfig, BenchlingTagConfigs} from 'llm/markdown/LLMMarkdownUtils';

// Custom tree traversal function to visit text nodes
const visitTextNodes = (
  node: Node,
  callback: (textNode: Text, index: number, parent: Parent) => void
): void => {
  const parent = node as Parent;
  if (parent.children != null && Array.isArray(parent.children)) {
    // Process children in reverse order to handle array modifications safely
    for (let i = parent.children.length - 1; i >= 0; i--) {
      const child = parent.children[i];
      if (child.type === 'text') {
        callback(child, i, parent);
      } else {
        visitTextNodes(child, callback);
      }
    }
  }
};

// Custom Remark plugin to handle tags when handling markdown syntax tree
export const remarkBenchlingTags =
  (options: BenchlingTagConfigs = {tagConfigs: []}) =>
  (tree: Node): void => {
    visitTextNodes(tree, (node: Text, index: number, parent: Parent): void => {
      const {replacements, hasMatches} = processTextNode(node, options.tagConfigs);

      if (hasMatches) {
        parent.children.splice(index, 1, ...replacements);
      }
    });
  };

interface ProcessTextNodeResult {
  replacements: Array<PhrasingContent>;
  hasMatches: boolean;
}

const processTextNode = (textNode: Text, tagConfigs: Array<BenchlingTagConfig>): ProcessTextNodeResult => {
  const text = textNode.value;
  const allMatches = findAllTagMatches(text, tagConfigs);

  if (allMatches.length === 0) {
    return {
      replacements: [],
      hasMatches: false,
    };
  }

  return {
    replacements: buildReplacementNodes(text, allMatches),
    hasMatches: true,
  };
};

interface TagMatch {
  start: number;
  end: number;
  content: string;
  tagName: string;
  isValid: boolean;
}

export const findAllTagMatches = (text: string, tagConfigs: Array<BenchlingTagConfig>): Array<TagMatch> => {
  const allMatches: Array<TagMatch> = [];

  for (const config of tagConfigs) {
    if (config.markdownTag == null) {
      continue;
    }

    const pattern = new RegExp(`<${config.markdownTag}>([\\s\\S]*?)</${config.markdownTag}>`, 'g');
    let match = pattern.exec(text);
    while (match != null) {
      const [fullMatch, content] = match;
      const isValid = config.validator(content);
      const tagMatch: TagMatch = {
        start: match.index,
        end: match.index + fullMatch.length,
        content,
        tagName: config.markdownTag,
        isValid,
      };

      allMatches.push(tagMatch);
      match = pattern.exec(text);
    }
  }

  return allMatches;
};

const buildReplacementNodes = (text: string, matches: Array<TagMatch>): Array<PhrasingContent> => {
  // Sort matches by position
  const sortedMatches = matches.sort((a, b) => a.start - b.start);

  const nodes: Array<PhrasingContent> = [];
  let lastIndex = 0;

  for (const match of sortedMatches) {
    if (match.start > lastIndex) {
      const beforeText = text.slice(lastIndex, match.start);
      if (beforeText) {
        nodes.push({
          type: 'text',
          value: beforeText,
        });
      }
    }

    if (match.isValid) {
      nodes.push({
        type: 'html',
        value: `<${match.tagName}>${match.content}</${match.tagName}>`,
      });
    } else {
      nodes.push({
        type: 'text',
        value: match.content,
      });
    }

    lastIndex = match.end;
  }

  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
      nodes.push({
        type: 'text',
        value: remainingText,
      });
    }
  }

  return nodes;
};

// Custom tree traversal function to visit code nodes
const visitCodeNodes = (node: Node, callback: (codeNode: Code) => void): void => {
  if (node.type === 'code') {
    callback(node as Code);
  }
  const parent = node as Parent;
  if (parent.children != null && Array.isArray(parent.children)) {
    for (const child of parent.children) {
      visitCodeNodes(child, callback);
    }
  }
};

/**
 * Remark plugin that transforms fenced code blocks into a custom `bnch_code` element.
 *
 * In the markdown AST, `code` nodes have `lang` and `value` as first-class properties.
 * This plugin changes the node type so the mdast-to-hast bridge doesn't apply its
 * built-in `code` handler (which wraps in `<pre>`), and instead uses `hName`/`hProperties`
 * to produce a custom `bnch_code` element with `language` and `code` as explicit props.
 */
export const remarkCodeBlock =
  () =>
  (tree: Node): void => {
    visitCodeNodes(tree, (node: Code) => {
      // Change the type so the mdast-to-hast bridge skips the built-in `code` handler
      // (which wraps in <pre>) and falls through to the generic hName/hProperties path.
      (node as Node).type = 'bnch_code';
      node.data = {
        hName: 'bnch_code',
        hProperties: {
          language: node.lang ?? 'text',
          code: node.value,
        },
      };
    });
  };
